/**
 * What `TableWrap` reads from its table (docs/specs/ui-layout-rules.md rule 5, decision 1026): each column's name from
 * the headings (a group's name before its own, "Flight hours (h), survey"), stamped on every cell as `data-label`, so
 * that under 900 px a table of more than four columns can show each row as a card of "name, value" lines; the cell's
 * part in a card (`data-card`: the row's key, its tick box, its buttons, or a cell across the whole row); the cells
 * that start the first column (`data-table-first`, kept in view while a wide table scrolls sideways); and how many
 * characters a long heading needs to wrap onto two lines at most (`--table-head-chars`).
 */

/** A sortable heading (a `SortHeader`), for the "Sort by" list above the cards. */
export interface SortableHead {
  /** The heading's place among the table's sortable headings. */
  readonly index: number;
  readonly label: string;
  readonly active: boolean;
  readonly dir: 'asc' | 'desc' | null;
}

export interface TableReading {
  /** The table's columns, from its headings (a heading over several columns counts each). */
  readonly columns: number;
  readonly sortable: readonly SortableHead[];
}

const SPACE = /\s+/g;

/** The words a sighted reader sees in a cell: its text, without screen-reader-only words. */
export function visibleText(el: Element): string {
  const parts: string[] = [];
  const walk = (node: Node): void => {
    for (const child of node.childNodes) {
      if (child.nodeType === 3) parts.push(child.textContent ?? '');
      else if (child.nodeType === 1) {
        const e = child as Element;
        if (e.classList.contains('sr-only') || e.tagName.toLowerCase() === 'svg') continue;
        walk(e);
      }
    }
  };
  walk(el);
  return parts.join(' ').replace(SPACE, ' ').trim();
}

/** The second part of a grouped name in lower case, unless it starts with an abbreviation ("ZFID"). */
function lowerFirst(s: string): string {
  return s.length > 1 && s[1] === s[1]!.toLowerCase() ? s[0]!.toLowerCase() + s.slice(1) : s;
}

/** The characters of the longer line when a heading's words are split into two lines as evenly as they go. */
export function twoLineChars(text: string): number {
  const words = text.split(' ').filter(Boolean);
  if (words.length < 2) return 0;
  const total = text.length;
  let best = total;
  let first = -1;
  for (let i = 0; i < words.length - 1; i++) {
    first += words[i]!.length + 1;
    best = Math.min(best, Math.max(first, total - first - 1));
  }
  return best;
}

/** The header cells over each column, top row first (a cell spanning rows or columns is listed in each), and the
 * column each header cell starts at. */
function headerGrid(table: HTMLTableElement): {
  readonly columns: HTMLTableCellElement[][];
  readonly starts: Map<HTMLTableCellElement, number>;
} {
  const columns: HTMLTableCellElement[][] = [];
  const starts = new Map<HTMLTableCellElement, number>();
  const taken: boolean[][] = [];
  const rows = table.tHead ? [...table.tHead.rows] : [];
  rows.forEach((row, r) => {
    let c = 0;
    for (const cell of row.cells) {
      while (taken[r]?.[c]) c++;
      starts.set(cell, c);
      const span = Math.max(1, cell.colSpan);
      const down = Math.max(1, cell.rowSpan);
      for (let dr = 0; dr < down; dr++)
        for (let dc = 0; dc < span; dc++) {
          (taken[r + dr] ??= [])[c + dc] = true;
          if (dr === 0) (columns[c + dc] ??= []).push(cell);
        }
      c += span;
    }
  });
  return { columns, starts };
}

function setAttr(el: Element, name: string, value: string | null): void {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
}

/** The column names, from the headings: the cell's own accessible name, or its group's words then its own. */
function columnNames(grid: HTMLTableCellElement[][]): string[] {
  return grid.map((cells) => {
    const own = cells[cells.length - 1];
    const named = own?.getAttribute('aria-label')?.trim();
    if (named) return named;
    const words = [...new Set(cells.map(visibleText).filter(Boolean))];
    return words.map((w, i) => (i === 0 ? w : lowerFirst(w))).join(', ');
  });
}

const CONTROL = 'button, a[href], select, input:not([type="checkbox"]), textarea';

/**
 * Stamps the table's cells (see the module's words) and returns what the "Sort by" list needs. Only attributes and
 * one style property change, so React's own rendering of the table is untouched.
 */
export function readTable(table: HTMLTableElement): TableReading {
  const { columns: grid, starts } = headerGrid(table);
  const columns = grid.length;
  const names = columnNames(grid);
  const hidden = names.map((n, c) => n === '' || grid[c]!.every((h) => visibleText(h) === ''));
  const key = hidden.findIndex((h) => !h);

  // headings: the first column's cell, and the room a long heading needs for two lines
  for (const [h, c] of starts) {
    setAttr(h, 'data-table-first', c === 0 && h.colSpan <= 1 ? '' : null);
    // a sortable heading's arrow takes about two characters more
    const chars = twoLineChars(visibleText(h)) + (h.querySelector('.sort-head') ? 2 : 0);
    if (chars > 2) h.style.setProperty('--table-head-chars', String(chars));
    else h.style.removeProperty('--table-head-chars');
  }

  // the rows: each cell's column name and its part in a card
  const bodyRows = [...table.tBodies]
    .flatMap((b) => [...b.rows])
    .concat(table.tFoot ? [...table.tFoot.rows] : []);
  for (const row of bodyRows) {
    let c = 0;
    for (const cell of row.cells) {
      const span = Math.max(1, cell.colSpan);
      let part: string | null = null;
      let label: string | null = names[c] ?? '';
      if (span >= columns) {
        part = 'full';
        label = null;
      } else if (cell.classList.contains('table__actions')) {
        part = 'actions';
        label = null;
      } else if (hidden[c] && span === 1) {
        const tick = cell.querySelector('input[type="checkbox"]');
        part =
          tick && !cell.querySelector(CONTROL) ? 'select' : cell.querySelector(CONTROL) ? 'actions' : null;
        label = null;
      } else if (c <= key && key < c + span) part = 'key';
      // a value across several columns (a total's name, "Loading…") has no one column's name
      if (span > 1) label = null;
      setAttr(cell, 'data-card', part);
      setAttr(cell, 'data-label', label || null);
      setAttr(cell, 'data-table-first', c === 0 && span === 1 ? '' : null);
      c += span;
    }
  }

  // the sortable headings, in their order across the table
  const sortable: SortableHead[] = [];
  const seen = new Set<Element>();
  for (const [c, cells] of grid.entries()) {
    const h = cells[cells.length - 1];
    if (!h || seen.has(h) || !h.querySelector('.sort-head')) continue;
    seen.add(h);
    const sort = h.getAttribute('aria-sort');
    sortable.push({
      index: sortable.length,
      label: names[c] || visibleText(h),
      active: sort === 'ascending' || sort === 'descending',
      dir: sort === 'ascending' ? 'asc' : sort === 'descending' ? 'desc' : null,
    });
  }
  return { columns, sortable };
}

/** The table's sortable headings' buttons, in the order `readTable` lists them. */
export function sortButtons(table: HTMLTableElement): HTMLButtonElement[] {
  const grid = headerGrid(table).columns;
  const seen = new Set<Element>();
  const out: HTMLButtonElement[] = [];
  for (const cells of grid) {
    const h = cells[cells.length - 1];
    const b = h?.querySelector<HTMLButtonElement>('.sort-head');
    if (!h || !b || seen.has(h)) continue;
    seen.add(h);
    out.push(b);
  }
  return out;
}
